import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import { hoyEcuador, inicioMesEcuador } from '../../utils/fechas';

const BASE = { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE };

const EMPTY_WORKER = {
    name: '',
    cedula: '',
    phone: '',
    commission_per_lunchbox: '0',
    commission_cafe_grano_pct: '0',
    commission_pos_pct: '0',
    notes: '',
    is_active: true
};

const LINEAS = [
    { id: 'lonchera', label: 'Loncheras', unit: 'loncheras' },
    { id: 'cafe_grano', label: 'Café en Grano', unit: 'fundas' },
    { id: 'cafeteria', label: 'Punto de Venta', unit: 'productos' }
];

// Fechas en hora de Ecuador
const today = hoyEcuador;
const firstDayOfMonth = inicioMesEcuador;
const money = (v) => `$${(parseFloat(v) || 0).toFixed(2)}`;

const cardStyle = {
    background: 'var(--card-bg, #ffffff)',
    borderRadius: '14px',
    border: '1px solid var(--border-color)',
    padding: '1.25rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
};
const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid var(--border-color)', fontSize: '14px', outline: 'none', background: 'white'
};
const labelStyle = { display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: 'var(--text-primary)' };
const th = { textAlign: 'left', padding: '10px 12px', fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', borderBottom: '1px solid var(--border-color)' };
const td = { padding: '10px 12px', fontSize: '14px', borderBottom: '1px solid var(--border-color)' };

const METHOD_LABELS = { efectivo: 'Efectivo', transferencia: 'Transferencia', tarjeta: 'Tarjeta' };
const LINE_LABELS = { lonchera: 'Loncheras', cafe_grano: 'Café en Grano', cafeteria: 'Punto de Venta' };
const formatDateTime = (value) => value
    ? new Date(value).toLocaleString('es-EC', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Guayaquil' })
    : '-';

// Capa de las ventanas: por encima del menú lateral (1000) y de la barra superior (999)
const MODAL_Z_INDEX = 1100;
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: MODAL_Z_INDEX + 10, padding: '1rem' };

/**
 * Estado de cuenta de un trabajador: ventas, fiados (a quién y cuánto deben),
 * cobros recibidos por forma de pago y comisiones generadas / pagadas.
 */
const DetalleTrabajador = ({ workerId, dateFrom, dateTo, setDateFrom, setDateTo, onBack }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [onlyPending, setOnlyPending] = useState(true);

    const [debtModal, setDebtModal] = useState(null); // { orderId, max }
    const [debtForm, setDebtForm] = useState({ amount: '', method: 'efectivo', reference: '', notes: '' });
    const [commissionModal, setCommissionModal] = useState(false);
    const [commissionForm, setCommissionForm] = useState({ amount: '', method: 'efectivo', notes: '' });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/api/orders/workers/${workerId}/statement/`, {
                ...BASE,
                params: { date_from: dateFrom, date_to: dateTo }
            });
            setData(res.data);
        } catch (err) {
            console.error('Error cargando estado de cuenta del trabajador:', err);
            alert('No se pudo cargar el detalle del trabajador.');
        } finally {
            setLoading(false);
        }
    }, [workerId, dateFrom, dateTo]);

    useEffect(() => { load(); }, [load]);

    const openDebtModal = (fiado = null) => {
        const max = fiado ? fiado.pending_amount : data.debt.pending_total;
        // El monto empieza vacío para que un abono no se sume al total escrito (ej. '30.0010')
        setDebtForm({ amount: '', method: 'efectivo', reference: '', notes: '' });
        setDebtModal({ orderId: fiado ? fiado.id : '', max, label: fiado ? fiado.debtor : null });
    };

    const submitDebtPayment = async (e) => {
        e.preventDefault();
        const amount = Math.round(parseFloat(debtForm.amount) * 100) / 100;
        if (!amount || amount <= 0) { alert('Ingresa un monto válido.'); return; }
        if (amount > debtModal.max + 0.009) { alert(`El monto ($${amount.toFixed(2)}) es mayor a lo que se debe ($${debtModal.max.toFixed(2)}).`); return; }
        const remainingAfter = Math.max(0, debtModal.max - amount);
        const confirmMessage = remainingAfter > 0.009
            ? `Registrar ABONO de $${amount.toFixed(2)}.\nQuedará debiendo $${remainingAfter.toFixed(2)}.\n\n¿Continuar?`
            : `Registrar PAGO TOTAL de $${amount.toFixed(2)}.\nLa deuda quedará en $0.00.\n\n¿Continuar?`;
        if (!window.confirm(confirmMessage)) return;
        setSaving(true);
        try {
            const res = await api.post(`/api/orders/workers/${workerId}/register_debt_payment/`, {
                ...debtForm,
                amount,
                order_id: debtModal.orderId || null
            }, BASE);
            alert(`${res.data.message}. Saldo pendiente del vendedor: $${res.data.remaining_debt.toFixed(2)}`);
            setDebtModal(null);
            load();
        } catch (err) {
            alert('No se pudo registrar el cobro: ' + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    const openCommissionModal = () => {
        const balance = Math.max(0, data.commission.balance);
        setCommissionForm({ amount: balance ? balance.toFixed(2) : '', method: 'efectivo', notes: `Comisión ${dateFrom} a ${dateTo}` });
        setCommissionModal(true);
    };

    const submitCommissionPayment = async (e) => {
        e.preventDefault();
        const amount = parseFloat(commissionForm.amount);
        if (!amount || amount <= 0) { alert('Ingresa un monto válido.'); return; }
        setSaving(true);
        try {
            const res = await api.post(`/api/orders/workers/${workerId}/register_commission_payment/`, {
                ...commissionForm,
                amount,
                period_from: dateFrom,
                period_to: dateTo
            }, BASE);
            alert(res.data.message);
            setCommissionModal(false);
            load();
        } catch (err) {
            alert('No se pudo registrar el pago: ' + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    const dateFilters = (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
                <label style={labelStyle}>Desde</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={inputStyle} />
            </div>
            <div>
                <label style={labelStyle}>Hasta</label>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={inputStyle} />
            </div>
            <button type="button" onClick={() => { setDateFrom(today()); setDateTo(today()); }} style={{ ...inputStyle, width: 'auto', cursor: 'pointer', fontWeight: '600' }}>Hoy</button>
            <button type="button" onClick={() => { setDateFrom(firstDayOfMonth()); setDateTo(today()); }} style={{ ...inputStyle, width: 'auto', cursor: 'pointer', fontWeight: '600' }}>Este mes</button>
        </div>
    );

    if (loading && !data) {
        return <p style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Cargando detalle del trabajador...</p>;
    }
    if (!data) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <button type="button" onClick={onBack} style={{ ...inputStyle, width: 'auto', cursor: 'pointer' }}>Cerrar</button>
            </div>
        );
    }

    const { worker, sales, sold_by_method: sold, debt, commission } = data;
    const fiados = onlyPending ? data.fiados.filter(f => f.pending_amount > 0) : data.fiados;
    const kpi = (label, value, color, sub) => (
        <div style={{ ...cardStyle, padding: '0.9rem 1rem' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{label}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: '800', color }}>{value}</div>
            {sub && <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{sub}</div>}
        </div>
    );
    const sectionTitle = (icon, text, extra) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className={`bi ${icon}`} style={{ color: 'var(--primary-color)' }}></i> {text}
            </h3>
            {extra}
        </div>
    );
    const primaryButton = (onClick, icon, text, disabled = false) => (
        <button type="button" onClick={onClick} disabled={disabled} style={{
            padding: '0.55rem 1rem', borderRadius: '10px', border: 'none', fontWeight: '700', cursor: disabled ? 'not-allowed' : 'pointer',
            background: disabled ? '#cbd5e1' : 'var(--primary-color)', color: 'white', display: 'flex', alignItems: 'center', gap: '0.4rem'
        }}>
            <i className={`bi ${icon}`}></i> {text}
        </button>
    );

    return (
        <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
            <div style={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex', justifyContent: 'flex-end', height: 0 }}>
                <button type="button" onClick={onBack} title="Cerrar (Esc)"
                    style={{ width: '38px', height: '38px', marginTop: '-6px', marginRight: '-6px', borderRadius: '50%', border: '1px solid var(--border-color)', background: 'white', cursor: 'pointer', fontSize: '16px', boxShadow: '0 2px 6px rgba(0,0,0,0.12)' }}>
                    <i className="bi bi-x-lg"></i>
                </button>
            </div>
            {/* Encabezado */}
            <div style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <div>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Detalle del trabajador</div>
                    <h2 style={{ margin: 0, fontSize: '1.4rem' }}>{worker.name}</h2>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                        {[worker.cedula && `CI: ${worker.cedula}`, worker.phone && `Tel: ${worker.phone}`].filter(Boolean).join(' · ') || 'Sin datos de contacto'}
                        {!worker.is_active && ' · Inactivo'}
                    </div>
                </div>
                {dateFilters}
            </div>

            {/* Ventas del período */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                {kpi('Ventas del período', money(sales.totals.total), 'var(--primary-color)', `${sales.totals.orders} venta(s)`)}
                {kpi('Cobrado en efectivo', money(sold.efectivo), '#15803d', 'al momento de vender')}
                {kpi('Cobrado por transferencia', money(sold.transferencia), '#1d4ed8', 'al momento de vender')}
                {kpi('Cobrado con tarjeta', money(sold.tarjeta), '#6b21a8', 'al momento de vender')}
                {kpi('Fiado en el período', money(sold.fiado), '#b91c1c', `${sales.totals.fiado_pendiente > 0 ? `${money(sales.totals.fiado_pendiente)} aún por cobrar` : 'todo cobrado'}`)}
            </div>

            {/* Fiados */}
            <div style={{ ...cardStyle, marginBottom: '1rem' }}>
                {sectionTitle('bi-journal-text', 'Fiados: a quién fió y cuánto deben',
                    primaryButton(() => openDebtModal(), 'bi-cash-coin', 'Registrar cobro', debt.pending_total <= 0)
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '0.9rem' }}>
                    {kpi('Deben ahora', money(debt.pending_total), '#b91c1c', `${debt.pending_orders} venta(s) con saldo`)}
                    {kpi('Abonado en efectivo', money(debt.collected_by_method.efectivo), '#15803d', 'en el período')}
                    {kpi('Abonado por transferencia', money(debt.collected_by_method.transferencia), '#1d4ed8', 'en el período')}
                    {kpi('Total abonado', money(debt.collected_total), 'var(--primary-color)', 'en el período')}
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', marginBottom: '0.5rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} />
                    Mostrar solo los que aún deben
                </label>
                <div style={{ overflowX: 'auto' }}>
                    {fiados.length === 0 ? (
                        <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '1rem' }}>
                            {onlyPending ? 'No hay fiados pendientes.' : 'No hay fiados en este período.'}
                        </p>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
                            <thead>
                                <tr>
                                    <th style={th}>Fecha</th>
                                    <th style={th}>Orden</th>
                                    <th style={th}>A quién</th>
                                    <th style={th}>Detalle</th>
                                    <th style={th}>Fiado</th>
                                    <th style={th}>Abonado</th>
                                    <th style={th}>Debe</th>
                                    <th style={th}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {fiados.map(f => (
                                    <tr key={f.id}>
                                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatDateTime(f.created_at)}</td>
                                        <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: '700' }}>{f.order_sequence ? `#${f.order_sequence}` : f.order_number}</td>
                                        <td style={{ ...td, fontWeight: '600' }}>{f.debtor}</td>
                                        <td style={{ ...td, fontSize: '13px', color: 'var(--text-secondary)' }}>
                                            <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{LINE_LABELS[f.business_type] || 'Venta'}</span> · {f.items_summary || '-'}
                                            {f.payments.length > 0 && (
                                                <div style={{ fontSize: '12px' }}>
                                                    Abonos: {f.payments.map(p => `${money(p.amount)} ${METHOD_LABELS[p.method] || p.method}`).join(', ')}
                                                </div>
                                            )}
                                        </td>
                                        <td style={td}>{money(f.credit_amount)}</td>
                                        <td style={{ ...td, color: '#15803d' }}>{money(f.collected)}</td>
                                        <td style={{ ...td, fontWeight: '800', color: f.pending_amount > 0 ? '#b91c1c' : '#15803d' }}>
                                            {f.pending_amount > 0 ? money(f.pending_amount) : 'Pagado'}
                                        </td>
                                        <td style={td}>
                                            {f.pending_amount > 0 && (
                                                <button type="button" onClick={() => openDebtModal(f)} style={{
                                                    padding: '5px 10px', borderRadius: '8px', border: '1px solid #16a34a',
                                                    background: '#f0fdf4', color: '#15803d', fontWeight: '700', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap'
                                                }}>
                                                    Cobrar
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Comisiones */}
            <div style={{ ...cardStyle, marginBottom: '1rem' }}>
                {sectionTitle('bi-percent', 'Comisiones', primaryButton(openCommissionModal, 'bi-wallet2', 'Pagar comisión'))}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '0.9rem' }}>
                    {kpi('Generada en el período', money(commission.earned_in_range), '#b45309',
                        `contado ${money(sales.totals.commission_contado)} · fiado ${money(sales.totals.commission_fiado)}`)}
                    {kpi('Pagada en el período', money(commission.paid_in_range), '#15803d')}
                    {kpi('Saldo por pagar', money(commission.balance), commission.balance > 0 ? '#b91c1c' : '#15803d',
                        `generado ${money(commission.earned_all_time)} · pagado ${money(commission.paid_all_time)} (total)`)}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem', marginBottom: '0.9rem' }}>
                    {LINEAS.map(linea => {
                        const l = sales.lines[linea.id];
                        if (!l || l.orders === 0) return null;
                        return (
                            <div key={linea.id} style={{ padding: '0.75rem', borderRadius: '10px', background: '#f8fafc', fontSize: '13px', lineHeight: 1.6 }}>
                                <div style={{ fontWeight: '700', color: 'var(--primary-color)' }}>{linea.label}</div>
                                <div>{l.units} {linea.unit} · {money(l.total)}</div>
                                <div style={{ color: '#15803d' }}>Contado: {l.units_contado} · {money(l.contado)}</div>
                                <div style={{ color: '#b91c1c' }}>Fiadas: {l.units_fiado} · {money(l.fiado)}</div>
                                <div style={{ color: '#b45309' }}>Comisión: {money(l.commission_contado + l.commission_fiado)}</div>
                            </div>
                        );
                    })}
                </div>
                {commission.payments.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
                            <thead>
                                <tr>
                                    <th style={th}>Fecha</th>
                                    <th style={th}>Monto</th>
                                    <th style={th}>Forma</th>
                                    <th style={th}>Nota</th>
                                    <th style={th}>Registró</th>
                                </tr>
                            </thead>
                            <tbody>
                                {commission.payments.map(p => (
                                    <tr key={p.id}>
                                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatDateTime(p.created_at)}</td>
                                        <td style={{ ...td, fontWeight: '700' }}>{money(p.amount)}</td>
                                        <td style={td}>{METHOD_LABELS[p.method] || p.method}</td>
                                        <td style={{ ...td, fontSize: '13px' }}>{p.notes || '-'}</td>
                                        <td style={{ ...td, fontSize: '13px', color: 'var(--text-secondary)' }}>{p.created_by_name || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Historial de cobros */}
            <div style={{ ...cardStyle }}>
                {sectionTitle('bi-clock-history', 'Historial de cobros del período')}
                {data.debt_payments.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '1rem' }}>No se registraron cobros en este período.</p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
                            <thead>
                                <tr>
                                    <th style={th}>Fecha</th>
                                    <th style={th}>Orden</th>
                                    <th style={th}>A quién</th>
                                    <th style={th}>Forma</th>
                                    <th style={th}>Monto</th>
                                    <th style={th}>Registró</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.debt_payments.map(p => (
                                    <tr key={p.id}>
                                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatDateTime(p.created_at)}</td>
                                        <td style={{ ...td, fontWeight: '700' }}>{p.order_sequence ? `#${p.order_sequence}` : p.order_number}</td>
                                        <td style={td}>{p.debtor}</td>
                                        <td style={td}>{METHOD_LABELS[p.method] || p.method}{p.reference ? ` · Ref ${p.reference}` : ''}</td>
                                        <td style={{ ...td, fontWeight: '700', color: '#15803d' }}>{money(p.amount)}</td>
                                        <td style={{ ...td, fontSize: '13px', color: 'var(--text-secondary)' }}>{p.created_by_name || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal: registrar cobro de fiado */}
            {debtModal && (
                <div style={modalOverlay} data-modal-nivel="2">
                    <form onSubmit={submitDebtPayment} style={{ ...cardStyle, width: '100%', maxWidth: '440px', background: 'white' }}>
                        <h3 style={{ marginTop: 0 }}>Registrar cobro de fiado</h3>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: 0 }}>
                            {debtModal.label
                                ? <>Venta de <strong>{debtModal.label}</strong>. Debe {money(debtModal.max)}.</>
                                : <>Se aplica a los fiados más antiguos primero. {worker.name} debe en total {money(debtModal.max)}.</>}
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                            <div>
                                <label style={labelStyle}>Monto ($)</label>
                                <input type="number" step="0.01" min="0.01" max={debtModal.max} value={debtForm.amount} placeholder="Ej: 10.00"
                                    onChange={(e) => setDebtForm({ ...debtForm, amount: e.target.value })} style={inputStyle} required autoFocus />
                                <button type="button" onClick={() => setDebtForm({ ...debtForm, amount: debtModal.max.toFixed(2) })}
                                    style={{ marginTop: '6px', padding: '4px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#f1f5f9', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>
                                    Cobrar todo ({money(debtModal.max)})
                                </button>
                                {parseFloat(debtForm.amount) > 0 && (() => {
                                    const remaining = debtModal.max - Math.round(parseFloat(debtForm.amount) * 100) / 100;
                                    if (remaining < -0.009) return <div style={{ marginTop: '6px', fontSize: '12px', fontWeight: '700', color: '#dc2626' }}>Es mayor a lo que se debe</div>;
                                    return (
                                        <div style={{ marginTop: '6px', fontSize: '12px', fontWeight: '700', color: remaining > 0.009 ? '#b45309' : '#15803d' }}>
                                            {remaining > 0.009 ? `Abono · quedará debiendo ${money(remaining)}` : 'Pago total · la deuda queda en $0.00'}
                                        </div>
                                    );
                                })()}
                            </div>
                            <div>
                                <label style={labelStyle}>Forma de pago</label>
                                <select value={debtForm.method} onChange={(e) => setDebtForm({ ...debtForm, method: e.target.value })} style={inputStyle}>
                                    <option value="efectivo">Efectivo</option>
                                    <option value="transferencia">Transferencia</option>
                                    <option value="tarjeta">Tarjeta</option>
                                </select>
                            </div>
                        </div>
                        {debtForm.method !== 'efectivo' && (
                            <div style={{ marginBottom: '12px' }}>
                                <label style={labelStyle}>Referencia</label>
                                <input value={debtForm.reference} onChange={(e) => setDebtForm({ ...debtForm, reference: e.target.value })} style={inputStyle} />
                            </div>
                        )}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Nota (opcional)</label>
                            <input value={debtForm.notes} onChange={(e) => setDebtForm({ ...debtForm, notes: e.target.value })} style={inputStyle} />
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button type="button" onClick={() => setDebtModal(null)} style={{ flex: 1, padding: '0.8rem', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'white', fontWeight: '600', cursor: 'pointer' }}>Cancelar</button>
                            <button type="submit" disabled={saving} style={{ flex: 1, padding: '0.8rem', borderRadius: '10px', border: 'none', background: '#16a34a', color: 'white', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer' }}>
                                {saving ? 'Guardando...' : 'Registrar cobro'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Modal: pagar comisión */}
            {commissionModal && (
                <div style={modalOverlay} data-modal-nivel="2">
                    <form onSubmit={submitCommissionPayment} style={{ ...cardStyle, width: '100%', maxWidth: '440px', background: 'white' }}>
                        <h3 style={{ marginTop: 0 }}>Pagar comisión a {worker.name}</h3>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: 0 }}>
                            Saldo de comisión por pagar: <strong>{money(commission.balance)}</strong>
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                            <div>
                                <label style={labelStyle}>Monto ($)</label>
                                <input type="number" step="0.01" min="0.01" value={commissionForm.amount}
                                    onChange={(e) => setCommissionForm({ ...commissionForm, amount: e.target.value })} style={inputStyle} required />
                            </div>
                            <div>
                                <label style={labelStyle}>Forma de pago</label>
                                <select value={commissionForm.method} onChange={(e) => setCommissionForm({ ...commissionForm, method: e.target.value })} style={inputStyle}>
                                    <option value="efectivo">Efectivo</option>
                                    <option value="transferencia">Transferencia</option>
                                </select>
                            </div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Nota</label>
                            <input value={commissionForm.notes} onChange={(e) => setCommissionForm({ ...commissionForm, notes: e.target.value })} style={inputStyle} />
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button type="button" onClick={() => setCommissionModal(false)} style={{ flex: 1, padding: '0.8rem', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'white', fontWeight: '600', cursor: 'pointer' }}>Cancelar</button>
                            <button type="submit" disabled={saving} style={{ flex: 1, padding: '0.8rem', borderRadius: '10px', border: 'none', background: 'var(--primary-color)', color: 'white', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer' }}>
                                {saving ? 'Guardando...' : 'Registrar pago'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

const Trabajadores = () => {
    const [tab, setTab] = useState('reporte');
    const [workers, setWorkers] = useState([]);
    const [loadingWorkers, setLoadingWorkers] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_WORKER);
    const [saving, setSaving] = useState(false);

    const [dateFrom, setDateFrom] = useState(firstDayOfMonth());
    const [dateTo, setDateTo] = useState(today());
    const [report, setReport] = useState([]);
    const [loadingReport, setLoadingReport] = useState(false);
    const [expanded, setExpanded] = useState(null);
    // Trabajador abierto en la vista de detalle (estado de cuenta)
    const [detailWorkerId, setDetailWorkerId] = useState(null);

    const loadWorkers = useCallback(async () => {
        setLoadingWorkers(true);
        try {
            const res = await api.get('/api/orders/workers/', BASE);
            setWorkers(Array.isArray(res.data) ? res.data : (res.data.results || []));
        } catch (err) {
            console.error('Error cargando trabajadores:', err);
            alert('No se pudieron cargar los trabajadores.');
        } finally {
            setLoadingWorkers(false);
        }
    }, []);

    const loadReport = useCallback(async () => {
        setLoadingReport(true);
        try {
            const res = await api.get('/api/orders/workers/sales_report/', {
                ...BASE,
                params: { date_from: dateFrom, date_to: dateTo }
            });
            setReport(res.data.workers || []);
        } catch (err) {
            console.error('Error cargando reporte de trabajadores:', err);
            alert('No se pudo cargar el reporte de ventas.');
        } finally {
            setLoadingReport(false);
        }
    }, [dateFrom, dateTo]);

    useEffect(() => { loadWorkers(); }, [loadWorkers]);

    const closeDetail = useCallback(() => {
        setDetailWorkerId(null);
        loadReport();
    }, [loadReport]);

    useEffect(() => {
        if (!detailWorkerId) return undefined;
        const onKey = (e) => {
            // Si hay una ventana de cobro / pago abierta encima, Escape no cierra el detalle
            if (e.key === 'Escape' && !document.querySelector('[data-modal-nivel="2"]')) closeDetail();
        };
        document.addEventListener('keydown', onKey);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previousOverflow;
        };
    }, [detailWorkerId, closeDetail]);
    useEffect(() => { loadReport(); }, [loadReport]);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY_WORKER);
        setShowModal(true);
    };

    const openEdit = (w) => {
        setEditing(w);
        setForm({
            name: w.name,
            cedula: w.cedula || '',
            phone: w.phone || '',
            commission_per_lunchbox: w.commission_per_lunchbox,
            commission_cafe_grano_pct: w.commission_cafe_grano_pct,
            commission_pos_pct: w.commission_pos_pct,
            notes: w.notes || '',
            is_active: w.is_active
        });
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            alert('El nombre es obligatorio.');
            return;
        }
        setSaving(true);
        const payload = {
            ...form,
            commission_per_lunchbox: parseFloat(form.commission_per_lunchbox) || 0,
            commission_cafe_grano_pct: parseFloat(form.commission_cafe_grano_pct) || 0,
            commission_pos_pct: parseFloat(form.commission_pos_pct) || 0
        };
        try {
            if (editing) {
                await api.patch(`/api/orders/workers/${editing.id}/`, payload, BASE);
            } else {
                await api.post('/api/orders/workers/', payload, BASE);
            }
            setShowModal(false);
            loadWorkers();
            loadReport();
        } catch (err) {
            console.error('Error guardando trabajador:', err);
            const data = err.response?.data;
            alert('Error al guardar: ' + (data ? JSON.stringify(data) : err.message));
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (w) => {
        try {
            await api.patch(`/api/orders/workers/${w.id}/`, { is_active: !w.is_active }, BASE);
            loadWorkers();
        } catch (err) {
            alert('No se pudo cambiar el estado: ' + err.message);
        }
    };

    const handleDelete = async (w) => {
        if (!window.confirm(`¿Eliminar a ${w.name}? Si tiene ventas registradas solo se desactivará.`)) return;
        try {
            const res = await api.delete(`/api/orders/workers/${w.id}/`, BASE);
            if (res.data?.message) alert(res.data.message);
            loadWorkers();
        } catch (err) {
            alert('No se pudo eliminar: ' + err.message);
        }
    };

    // Filas del reporte: vendedores activos con ventas + vendedores activos sin ventas en el período (en $0).
    // Los inactivos no se listan ni suman en los totales. El estado se toma de la lista de trabajadores
    // (que se recarga al activar/desactivar), no del reporte, que puede haberse cargado antes del cambio.
    const visibleReport = useMemo(() => {
        const activeById = new Map(workers.map(w => [w.id, w.is_active]));
        return report.filter(r => {
            if (!r.worker_id) return true; // ventas sin vendedor asignado
            return activeById.has(r.worker_id) ? activeById.get(r.worker_id) : r.is_active !== false;
        });
    }, [report, workers]);

    const reportRows = useMemo(() => {
        const zeroLine = { orders: 0, total: 0, contado: 0, fiado: 0, fiado_cobrado: 0, fiado_pendiente: 0, units: 0, units_contado: 0, units_fiado: 0, grams: 0, commission_contado: 0, commission_fiado: 0 };
        const withSales = new Set(report.map(r => r.worker_id));
        const withoutSales = workers
            .filter(w => w.is_active && !withSales.has(w.id))
            .map(w => ({
                worker_id: w.id,
                worker_name: w.name,
                is_active: true,
                lines: { lonchera: zeroLine, cafe_grano: zeroLine, cafeteria: zeroLine },
                totals: { orders: 0, total: 0, contado: 0, fiado: 0, fiado_cobrado: 0, fiado_pendiente: 0, commission_contado: 0, commission_fiado: 0 }
            }));
        const unassigned = visibleReport.filter(r => !r.worker_id);
        return [...visibleReport.filter(r => r.worker_id), ...withoutSales, ...unassigned];
    }, [report, visibleReport, workers]);

    const totalsAll = visibleReport.reduce((acc, r) => {
        acc.total += r.totals.total;
        acc.contado += r.totals.contado;
        acc.fiado += r.totals.fiado;
        acc.commission += r.totals.commission_contado + r.totals.commission_fiado;
        return acc;
    }, { total: 0, contado: 0, fiado: 0, commission: 0 });

    const tabButton = (id, label, icon) => (
        <button
            type="button"
            onClick={() => setTab(id)}
            style={{
                padding: '0.6rem 1.1rem', borderRadius: '10px', fontWeight: '700', cursor: 'pointer',
                border: `1px solid ${tab === id ? 'var(--primary-color)' : 'var(--border-color)'}`,
                background: tab === id ? 'var(--primary-color)' : 'white',
                color: tab === id ? 'white' : 'var(--text-secondary)',
                display: 'flex', alignItems: 'center', gap: '0.4rem'
            }}
        >
            <i className={`bi ${icon}`}></i> {label}
        </button>
    );

    return (
        <div style={{ padding: '1rem', maxWidth: '1400px', margin: '0 auto', color: 'var(--text-color)' }}>
            <div className="page-header-banner">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                        <i className="bi bi-person-badge-fill" style={{ fontSize: '1.75rem' }}></i>
                        <h1 style={{ margin: 0, fontSize: '1.65rem', fontWeight: 'bold', letterSpacing: '-0.5px' }}>
                            Trabajadores
                        </h1>
                    </div>
                    <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem', maxWidth: '650px' }}>
                        Registra a tu equipo de ventas, consulta cuánto vendió cada uno (al contado y fiado) y calcula sus comisiones.
                    </p>
                </div>
                <button
                    onClick={openCreate}
                    style={{
                        background: 'var(--secondary-color)', color: 'var(--primary-color)', border: 'none',
                        padding: '0.65rem 1.25rem', borderRadius: '10px', fontWeight: '700', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                >
                    <i className="bi bi-plus-circle-fill"></i> Nuevo Trabajador
                </button>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', margin: '1rem 0', flexWrap: 'wrap' }}>
                {tabButton('reporte', 'Ventas y Comisiones', 'bi-graph-up')}
                {tabButton('lista', 'Lista de Trabajadores', 'bi-people')}
            </div>

            {tab === 'reporte' && (
                <>
                    <div style={{ ...cardStyle, display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
                        <div>
                            <label style={labelStyle}>Desde</label>
                            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Hasta</label>
                            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={inputStyle} />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button type="button" onClick={() => { setDateFrom(today()); setDateTo(today()); }} style={{ ...inputStyle, width: 'auto', cursor: 'pointer', fontWeight: '600' }}>Hoy</button>
                            <button type="button" onClick={() => { setDateFrom(firstDayOfMonth()); setDateTo(today()); }} style={{ ...inputStyle, width: 'auto', cursor: 'pointer', fontWeight: '600' }}>Este mes</button>
                        </div>
                    </div>

                    <div className="kpi-grid-responsive" style={{ marginBottom: '1rem' }}>
                        {[
                            { label: 'Total vendido', value: money(totalsAll.total), color: 'var(--primary-color)' },
                            { label: 'Al contado', value: money(totalsAll.contado), color: '#15803d' },
                            { label: 'Fiado', value: money(totalsAll.fiado), color: '#b91c1c' },
                            { label: 'Comisiones', value: money(totalsAll.commission), color: '#b45309' }
                        ].map(k => (
                            <div key={k.label} style={cardStyle}>
                                <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{k.label}</div>
                                <div style={{ fontSize: '1.6rem', fontWeight: '800', color: k.color }}>{k.value}</div>
                            </div>
                        ))}
                    </div>

                    <div style={{ ...cardStyle, padding: 0, overflowX: 'auto' }}>
                        {loadingReport ? (
                            <p style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Cargando reporte...</p>
                        ) : reportRows.length === 0 ? (
                            <p style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                No hay vendedores ni ventas. Agrega trabajadores con <strong>Nuevo Trabajador</strong>.
                            </p>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
                                <thead>
                                    <tr>
                                        <th style={th}>Trabajador</th>
                                        <th style={th}>Ventas</th>
                                        <th style={th}>Total</th>
                                        <th style={th}>Contado</th>
                                        <th style={th}>Fiado</th>
                                        <th style={th}>Fiado pendiente</th>
                                        <th style={th}>Comisión contado</th>
                                        <th style={th}>Comisión fiado</th>
                                        <th style={th}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportRows.map(r => {
                                        const key = r.worker_id || 'none';
                                        const isOpen = expanded === key;
                                        return (
                                            <React.Fragment key={key}>
                                                <tr style={{ background: r.worker_id ? 'transparent' : '#fffbeb' }}>
                                                    <td style={{ ...td, fontWeight: '700' }}>
                                                        {r.worker_name}
                                                        {r.is_active === false && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#94a3b8' }}>(inactivo)</span>}
                                                    </td>
                                                    <td style={td}>{r.totals.orders}</td>
                                                    <td style={{ ...td, fontWeight: '700' }}>{money(r.totals.total)}</td>
                                                    <td style={{ ...td, color: '#15803d' }}>{money(r.totals.contado)}</td>
                                                    <td style={{ ...td, color: '#b91c1c' }}>{money(r.totals.fiado)}</td>
                                                    <td style={td}>{money(r.totals.fiado_pendiente)}</td>
                                                    <td style={{ ...td, fontWeight: '700', color: '#b45309' }}>{r.worker_id ? money(r.totals.commission_contado) : '-'}</td>
                                                    <td style={{ ...td, color: '#b45309' }}>{r.worker_id ? money(r.totals.commission_fiado) : '-'}</td>
                                                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                                        <button type="button" onClick={() => setExpanded(isOpen ? null : key)}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary-color)', fontWeight: '600' }}>
                                                            {isOpen ? 'Ocultar' : 'Por línea'} <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                                        </button>
                                                        {r.worker_id && (
                                                            <button type="button" onClick={() => setDetailWorkerId(r.worker_id)}
                                                                style={{ marginLeft: '6px', padding: '4px 10px', borderRadius: '8px', border: '1px solid var(--primary-color)', background: 'white', color: 'var(--primary-color)', fontWeight: '700', cursor: 'pointer', fontSize: '12px' }}>
                                                                Ver detalle
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                                {isOpen && (
                                                    <tr>
                                                        <td colSpan={9} style={{ ...td, background: '#f8fafc' }}>
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '0.75rem' }}>
                                                                {LINEAS.map(linea => {
                                                                    const l = r.lines[linea.id];
                                                                    if (!l || l.orders === 0) return null;
                                                                    return (
                                                                        <div key={linea.id} style={{ ...cardStyle, padding: '0.9rem' }}>
                                                                            <div style={{ fontWeight: '700', marginBottom: '6px', color: 'var(--primary-color)' }}>{linea.label}</div>
                                                                            <div style={{ fontSize: '13px', lineHeight: 1.7 }}>
                                                                                <div>{l.orders} venta(s) · {l.units} {linea.unit}{linea.id === 'cafe_grano' && l.grams ? ` (${l.grams.toLocaleString()} g)` : ''}</div>
                                                                                <div style={{ color: '#15803d' }}>Vendidas al contado: <strong>{l.units_contado}</strong> · {money(l.contado)}</div>
                                                                                <div style={{ color: '#b91c1c' }}>Fiadas: <strong>{l.units_fiado}</strong> · {money(l.fiado)}</div>
                                                                                {l.fiado > 0 && (
                                                                                    <div style={{ color: 'var(--text-secondary)' }}>De lo fiado: cobrado {money(l.fiado_cobrado)} · pendiente {money(l.fiado_pendiente)}</div>
                                                                                )}
                                                                                {r.worker_id && (
                                                                                    <div style={{ color: '#b45309' }}>Comisión: contado {money(l.commission_contado)} · fiado {money(l.commission_fiado)}</div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '0.75rem' }}>
                        En pagos mixtos, las unidades fiadas se estiman en proporción al monto fiado. Las ventas sin vendedor asignado no generan comisión.
                    </p>
                </>
            )}

            {tab === 'lista' && (
                <div style={{ ...cardStyle, padding: 0, overflowX: 'auto' }}>
                    {loadingWorkers ? (
                        <p style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Cargando trabajadores...</p>
                    ) : workers.length === 0 ? (
                        <p style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                            Aún no hay trabajadores. Pulsa <strong>Nuevo Trabajador</strong> para agregar el primero.
                        </p>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
                            <thead>
                                <tr>
                                    <th style={th}>Nombre</th>
                                    <th style={th}>Cédula</th>
                                    <th style={th}>Teléfono</th>
                                    <th style={th}>Por lonchera</th>
                                    <th style={th}>Café en grano</th>
                                    <th style={th}>Punto de venta</th>
                                    <th style={th}>Estado</th>
                                    <th style={th}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {workers.map(w => (
                                    <tr key={w.id} style={{ opacity: w.is_active ? 1 : 0.55 }}>
                                        <td style={{ ...td, fontWeight: '700' }}>{w.name}</td>
                                        <td style={td}>{w.cedula || '-'}</td>
                                        <td style={td}>{w.phone || '-'}</td>
                                        <td style={td}>{money(w.commission_per_lunchbox)}</td>
                                        <td style={td}>{parseFloat(w.commission_cafe_grano_pct)}%</td>
                                        <td style={td}>{parseFloat(w.commission_pos_pct)}%</td>
                                        <td style={td}>
                                            <span style={{
                                                padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                                                background: w.is_active ? '#dcfce7' : '#f1f5f9', color: w.is_active ? '#15803d' : '#64748b'
                                            }}>
                                                {w.is_active ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                            <button type="button" onClick={() => setDetailWorkerId(w.id)}
                                                style={{ marginRight: '6px', padding: '4px 10px', borderRadius: '8px', border: '1px solid var(--primary-color)', background: 'white', color: 'var(--primary-color)', fontWeight: '700', cursor: 'pointer', fontSize: '12px' }}>
                                                <i className="bi bi-eye"></i> Ver detalle
                                            </button>
                                            <button type="button" title="Editar" onClick={() => openEdit(w)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary-color)', fontSize: '16px' }}>
                                                <i className="bi bi-pencil-square"></i>
                                            </button>
                                            <button type="button" title={w.is_active ? 'Desactivar' : 'Activar'} onClick={() => handleToggleActive(w)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '16px' }}>
                                                <i className={`bi ${w.is_active ? 'bi-toggle-on' : 'bi-toggle-off'}`}></i>
                                            </button>
                                            <button type="button" title="Eliminar" onClick={() => handleDelete(w)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '16px' }}>
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* Detalle del trabajador en una ventana aparte.
                Se dibuja en <body> (portal) para quedar encima del menú lateral y de la barra superior. */}
            {detailWorkerId && createPortal(
                <div
                    onClick={(e) => { if (e.target === e.currentTarget) closeDetail(); }}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: MODAL_Z_INDEX, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '2vh 1rem', overflowY: 'auto' }}
                >
                    <div style={{ width: '100%', maxWidth: '1200px', background: '#f8fafc', borderRadius: '16px', padding: '1rem', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
                        <DetalleTrabajador
                            workerId={detailWorkerId}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                            setDateFrom={setDateFrom}
                            setDateTo={setDateTo}
                            onBack={closeDetail}
                        />
                    </div>
                </div>,
                document.body
            )}

            {showModal && createPortal(
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: MODAL_Z_INDEX, padding: '1rem' }}>
                    <form onSubmit={handleSave} style={{ ...cardStyle, width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', background: 'white' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h3 style={{ margin: 0 }}>{editing ? 'Editar Trabajador' : 'Nuevo Trabajador'}</h3>
                            <button type="button" onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#94a3b8' }}>
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                            <label style={labelStyle}>Nombre completo *</label>
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required style={inputStyle} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                            <div>
                                <label style={labelStyle}>Cédula</label>
                                <input value={form.cedula} onChange={(e) => setForm({ ...form, cedula: e.target.value })} style={inputStyle} />
                            </div>
                            <div>
                                <label style={labelStyle}>Teléfono</label>
                                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={inputStyle} />
                            </div>
                        </div>

                        <div style={{ padding: '12px', background: '#fffbeb', borderRadius: '10px', border: '1px solid #fde68a', marginBottom: '12px' }}>
                            <div style={{ fontWeight: '700', color: '#92400e', marginBottom: '8px', fontSize: '13px' }}>Comisiones</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
                                <div>
                                    <label style={labelStyle}>Por lonchera ($)</label>
                                    <input type="number" step="0.01" min="0" value={form.commission_per_lunchbox} onChange={(e) => setForm({ ...form, commission_per_lunchbox: e.target.value })} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Café en grano (%)</label>
                                    <input type="number" step="0.01" min="0" value={form.commission_cafe_grano_pct} onChange={(e) => setForm({ ...form, commission_cafe_grano_pct: e.target.value })} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Punto de venta (%)</label>
                                    <input type="number" step="0.01" min="0" value={form.commission_pos_pct} onChange={(e) => setForm({ ...form, commission_pos_pct: e.target.value })} style={inputStyle} />
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                            <label style={labelStyle}>Notas</label>
                            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', marginBottom: '16px' }}>
                            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Activo (aparece en "Vendido por")
                        </label>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: '0.8rem', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'white', fontWeight: '600', cursor: 'pointer' }}>
                                Cancelar
                            </button>
                            <button type="submit" disabled={saving} style={{ flex: 1, padding: '0.8rem', borderRadius: '10px', border: 'none', background: 'var(--primary-color)', color: 'white', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer' }}>
                                {saving ? 'Guardando...' : 'Guardar'}
                            </button>
                        </div>
                    </form>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Trabajadores;
